import { useEffect, useState } from "react";
import { Building2, Star, ShieldCheck, BadgeCheck, Check, X, Handshake } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function AdminCompanies() {
  const [claims, setClaims] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.all([
      api.get("/admin/company-claims").then((r) => setClaims(r.data)).catch(() => {}),
      api.get("/admin/companies").then((r) => setCompanies(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const resolveClaim = async (id, approve) => {
    await api.post(`/admin/company-claims/${id}/resolve?approve=${approve}`);
    toast.success(approve ? "Revendicare aprobată." : "Revendicare respinsă.");
    load();
  };

  const patch = async (c, updates) => {
    setCompanies((list) => list.map((x) => (x.id === c.id ? { ...x, ...updates } : x)));
    try {
      await api.post(`/admin/companies/${c.id}/rating`, updates);
      toast.success("Actualizat.");
    } catch { toast.error("Eroare la salvare."); load(); }
  };

  if (loading) return <div className="border border-slate-200 p-8 text-center text-slate-400">Se încarcă...</div>;

  return (
    <div data-testid="admin-companies">
      {/* Pending claims */}
      <h2 className="font-bold text-lg mb-1 flex items-center gap-2"><Handshake className="w-5 h-5" /> Revendicări în așteptare ({claims.length})</h2>
      <p className="text-sm text-slate-500 mb-4">Companii reale care solicită preluarea unui profil.</p>
      {claims.length === 0 ? (
        <div className="border border-slate-200 p-6 text-center text-slate-400 text-sm mb-10" data-testid="no-claims">Nicio revendicare în așteptare.</div>
      ) : (
        <div className="border border-slate-200 divide-y divide-slate-100 mb-10">
          {claims.map((c) => (
            <div key={c.id} className="flex items-center gap-4 p-4" data-testid={`claim-${c.id}`}>
              <div className="w-10 h-10 bg-slate-900 text-white flex items-center justify-center shrink-0"><Building2 className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{c.company_name}</div>
                <div className="text-xs text-slate-500">Solicitant: {c.user_email}</div>
              </div>
              <button onClick={() => resolveClaim(c.id, true)} className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 text-xs font-bold uppercase transition-colors" data-testid={`claim-approve-${c.id}`}><Check className="w-4 h-4" /> Aprobă</button>
              <button onClick={() => resolveClaim(c.id, false)} className="inline-flex items-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 px-3 py-2 text-xs font-bold uppercase transition-colors" data-testid={`claim-reject-${c.id}`}><X className="w-4 h-4" /> Respinge</button>
            </div>
          ))}
        </div>
      )}

      {/* Companies rating management */}
      <h2 className="font-bold text-lg mb-4 flex items-center gap-2"><Star className="w-5 h-5" /> Companii & rating ({companies.length})</h2>
      <div className="border border-slate-200 divide-y divide-slate-100">
        {companies.map((c) => (
          <div key={c.id} className="flex flex-col md:flex-row md:items-center gap-3 p-4" data-testid={`admin-company-${c.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{c.company_name}</div>
              <div className="text-xs text-slate-500">{c.niche || "—"} · {c.county || "—"} · {c.email}</div>
            </div>
            {/* Stars */}
            <div className="flex items-center gap-1" data-testid={`rating-${c.id}`}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => patch(c, { trust_rating: n === c.trust_rating ? 0 : n })} className="p-1" title={`${n} stele`} data-testid={`rating-star-${c.id}-${n}`}>
                  <Star className={`w-5 h-5 transition-colors ${n <= c.trust_rating ? "text-amber-500 fill-amber-500" : "text-slate-300 hover:text-amber-300"}`} />
                </button>
              ))}
            </div>
            {/* Toggles */}
            <button onClick={() => patch(c, { verified: !c.verified })}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide border transition-colors ${c.verified ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-300 text-slate-500 hover:border-emerald-500"}`}
              data-testid={`toggle-verified-${c.id}`}><ShieldCheck className="w-3.5 h-3.5" /> Verificat</button>
            <button onClick={() => patch(c, { oxo_endorsed: !c.oxo_endorsed })}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide border transition-colors ${c.oxo_endorsed ? "bg-amber-500 text-slate-900 border-amber-500" : "border-slate-300 text-slate-500 hover:border-amber-500"}`}
              data-testid={`toggle-endorsed-${c.id}`}><BadgeCheck className="w-3.5 h-3.5" /> OXO Endorsed</button>
          </div>
        ))}
      </div>
    </div>
  );
}
